import React, { memo, useCallback, useContext, useEffect, useRef } from 'react'
import {
  ModalPromptContext,
  OK_CANCEL_PROMPT_CHOICES,
  CHOICE_ID_CANCEL,
  CHOICE_ID_OK
} from '@purecarslabs/signal-components/ModalPrompt'
import { ModalPromptCustomReturnType } from '@purecarslabs/signal-components/ModalPrompt/types'
/* webpack-strip-block:removed */
import FormContent from '@app-src/containers/FormContent'
import { FormContentPropsType } from '@app-src/containers/FormContent/types'
import { NativeLeadType } from '@app-src/data/leads/types.ts'
import { debugLogForms } from '@app-src/debug'
import { FormTriggerPropsType } from '@app-src/types'
import {
  ModalStyleOverride,
  /* webpack-strip-block:removed */
} from './styles'

// allows you to pass parameters to the popover you supply to promptCustom
const makeFormPopoverContent = (
  props: FormContentPropsType
): React.FC => () => <FormContent {...props} />

// this is the form trigger component, which handles owning the modal which is generated containing
// the form
export const ModalTrigger: React.FC<FormTriggerPropsType> = ({
  activeFormDetails,
  formLoaded,
  mvhColors,
  onAfterSubmitLead,
  setActiveFormDetails,
  ...rest
}) => {
  // hooks, state, and refs
  /////////////////////////

  const { promptCustom } = useContext(ModalPromptContext)
  const modalPromptReturnRef = useRef<ModalPromptCustomReturnType | undefined>()
  const submitFormikRef = useRef<((evt: React.FormEvent) => void) | undefined>()

  // callbacks
  ////////////

  const handleNeedEnableChoice = useCallback(
    () => modalPromptReturnRef.current?.enableChoice,
    []
  )

  const handleAfterSubmitLead = useCallback(
    (lead: NativeLeadType) => {
      modalPromptReturnRef.current?.dismiss(CHOICE_ID_OK)
      onAfterSubmitLead(lead)
      submitFormikRef.current = undefined
    },
    [onAfterSubmitLead]
  )

  const handleChoice = useCallback(
    (choiceId: string) => {
      debugLogForms('saw modal form choice', choiceId)

      if (choiceId === CHOICE_ID_CANCEL) {
        // cancel dismisses
        return true
      }
      // if not cancelling and we still have a formik ref, then submit the form via formik
      if (submitFormikRef.current) {
        // @ts-ignore
        submitFormikRef.current()
        // do not dismiss if submitting
        return false
      }
      // no formik ref - should be normal post-submit dismissal
      return true
    },
    [submitFormikRef]
  )

  // effects
  //////////

  // this effect causes the prompt to be shown if the API has been called to set active form details
  useEffect(() => {
    if (activeFormDetails) {
      debugLogForms('prompting modal')

      const modalPromptReturn = promptCustom({
        buttonVariant: 'contained',
        choices: OK_CANCEL_PROMPT_CHOICES,
        defaultChoiceId: CHOICE_ID_CANCEL,
        DescriptionComponent: makeFormPopoverContent({
          ...activeFormDetails,
          mvhColors,
          onNeedEnableChoice: handleNeedEnableChoice,
          onAfterSubmitLead: handleAfterSubmitLead,
          submitFormikRef,
          /* webpack-strip-block:removed */
          ...rest
        }),
        disableAutoFocus: true,
        disableEnforceFocus: true,
        onChoice: handleChoice
      })

      modalPromptReturnRef.current = modalPromptReturn

      // wait for modal to dismiss, then clear the active form details
      // the showForm() API is waiting for the active form to be set to undefined
      // before it can resolve
      modalPromptReturn.result.then(() => {
        // this will cause the API call which generates the modal to resolve
        debugLogForms('dismissing lead modal')
        setActiveFormDetails(undefined)
      })
    }
  }, [
    activeFormDetails,
    handleAfterSubmitLead,
    handleChoice,
    handleNeedEnableChoice,
    mvhColors,
    promptCustom,
    setActiveFormDetails,
    submitFormikRef,
    rest
  ])

  /* eslint-disable prettier/prettier */
  const ModalStyleOverrideUse = 
    /* webpack-strip-block:removed */
    ModalStyleOverride
  /* eslint-enable prettier/prettier */
  return (
    <ModalStyleOverrideUse $formLoaded={formLoaded} $mvhColors={mvhColors} />
  )
}

export default memo(ModalTrigger)
